import React, { useEffect, useRef, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";

const TYP_COLOR = {
  warnschwelle:            "#8A5A00",
  entwurf_upload:          "#BE2E6C",
  freigabe_ergebnis:       "#157A4A",
  designer_profil_update:  "#0E62C4",
  chat:                    "#1B1E23",
};

function linkFor(n) {
  if (n.projekt_id) return `/projekte/${n.projekt_id}`;
  if (n.designer_id) return `/designer`;
  return "/";
}

export default function NotificationsBell() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const typLabel = (typ) => {
    const key = `notif.${typ}`;
    const v = t(key);
    return v === key ? typ : v;
  };

  const loadCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch {}
  };
  const loadList = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } catch {}
  };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) loadList();
  }, [open]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setCount(0);
    setItems((s) => s.map((n) => ({ ...n, gelesen: true })));
  };

  const clickItem = async (n) => {
    if (!n.gelesen) {
      await api.post(`/notifications/${n.id}/read`);
      setCount((c) => Math.max(0, c - 1));
      setItems((s) => s.map((x) => (x.id === n.id ? { ...x, gelesen: true } : x)));
    }
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="bell-btn"
        className="relative p-2 rounded-sm hover:bg-line/50"
        aria-label={t("notif.title")}
      >
        <Bell size={18} strokeWidth={1.75} />
        {count > 0 && (
          <span
            data-testid="bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-brunnerMagenta text-white text-[10px] font-mono flex items-center justify-center px-1"
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 bg-white border border-line rounded-md z-50"
          data-testid="bell-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <div className="font-archivo font-bold text-sm">{t("notif.title")}</div>
            {count > 0 && (
              <button onClick={markAll} data-testid="mark-all-read" className="mono-label hover:opacity-100">{t("notif.mark_all")}</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-ink/50">{t("notif.empty")}</div>
            )}
            {items.map((n) => {
              const color = TYP_COLOR[n.typ] || "#1B1E23";
              return (
                <Link
                  key={n.id}
                  to={linkFor(n)}
                  onClick={() => clickItem(n)}
                  data-testid={`notif-${n.id}`}
                  className={`block px-4 py-3 border-b border-line last:border-0 hover:bg-paper/60 transition-colors ${
                    !n.gelesen ? "bg-paper/40" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 mt-2 rounded-full shrink-0" style={{ backgroundColor: n.gelesen ? "#DEDCD4" : color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="mono-label" style={{ color, opacity: 1 }}>{typLabel(n.typ)}</span>
                        <span className="mono-label">{fmtDate(n.erstellt_am)}</span>
                      </div>
                      <div className="text-sm mt-1">
                        {n.titel || (n.typ === "warnschwelle"
                          ? t("notif.threshold_reached").replace("{schwelle}", n.schwelle).replace("{ist}", n.ist_prozent)
                          : t("notif.new_activity"))}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
